import { TablePagination } from "components/paginations/TablePagination"

import { CardWrapper } from "features/user/cards/CardWrapper"

import { Provider, ProviderGeoRestriction } from "services/providers/type"

interface Props {
  rows: Provider[]
  restrictions: ProviderGeoRestriction[]
  onToggle: (row: Provider) => void
  onChangeFeeRate: (row: Provider, nextPercent: number) => void
  onOpenGeo: (row: Provider) => void
  page: number
  totalPages: number
  setPage: (n: number) => void
  canManageFees: boolean
}

function GeoCell({ restriction, onOpen }: { provider: Provider; restriction: ProviderGeoRestriction | undefined; onOpen: () => void }) {
  if (!restriction) {
    return (
      <button type="button" title="Add GEO restrictions" onClick={onOpen} className="btn btn-xs btn-ghost opacity-50 hover:opacity-100">
        +
      </button>
    )
  }

  const total = restriction.blocked_countries.length

  return (
    <button type="button" title="View / edit GEO restrictions" onClick={onOpen} className="flex items-center gap-1.5 group">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="w-4 h-4 opacity-50 group-hover:opacity-100"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
      <span className="text-xs opacity-60 group-hover:opacity-100">{total}</span>
    </button>
  )
}

export function ProviderTable({ rows, restrictions, onToggle, onChangeFeeRate, onOpenGeo, page, totalPages, setPage, canManageFees }: Props) {
  if (!rows.length) return <p className="py-8 text-center text-[rgba(6,7,21,1)] dark:text-white">No providers found.</p>

  const onJump = (pageNumber: number) => setPage(pageNumber)

  const handleFeeRateInputChange = (row: Provider, value: string) => {
    const parsed = parseFloat(value)
    onChangeFeeRate(row, parsed)
  }

  const restrictionFor = (id: string) => restrictions.find((r) => r.provider_id === id)

  return (
    <CardWrapper>
      <header className="flex items-center justify-between px-3 pb-4">
        <h2 className="font-semibold text-2xl capitalize text-[rgba(6,7,21,1)] dark:text-white">Providers</h2>
      </header>

      {/* MOBILE */}
      <div className="md:hidden flex flex-col gap-3 mb-4">
        {rows.map((r, i) => (
          <div
            key={r.identifier}
            className="rounded-xl border border-black/10 dark:border-white/20 bg-white dark:bg-transparent p-3 text-[rgba(6,7,21,1)] dark:text-white"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs opacity-60">#{(page - 1) * rows.length + i + 1}</span>
                  <span className="text-xs opacity-60 break-all">{r.identifier}</span>
                </div>

                <div className="mt-1 font-semibold text-base truncate">{r.title}</div>
                <div className="text-sm opacity-80 truncate">{r.provider}</div>
              </div>

              <div className="shrink-0 flex items-center gap-2">
                <input type="checkbox" className="toggle toggle-success" checked={r.enabled} onChange={() => onToggle(r)} />
              </div>
            </div>

            {(r.logo || r.slider_image) && (
              <div className="mt-3 flex items-center gap-4">
                {r.logo && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs opacity-60">Logo</span>
                    <img src={r.logo} alt={r.title} className="h-6" />
                  </div>
                )}

                {r.slider_image && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs opacity-60">Slider</span>
                    <img src={r.slider_image} alt={r.title} className="h-6" />
                  </div>
                )}
              </div>
            )}

            <div className="mt-3 flex items-center gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-xs opacity-60">GEO</span>
                <GeoCell provider={r} restriction={restrictionFor(r.identifier)} onOpen={() => onOpenGeo(r)} />
              </div>

              {canManageFees && (
                <div className="flex-1">
                  <div className="text-xs opacity-60 mb-1">Fee rate (%)</div>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    className="input input-bordered w-full bg-white text-black border-black/10 dark:bg-[#0C0C1A] dark:text-white dark:border-white/10"
                    value={Number.isFinite(r.feeRate) ? (r.feeRate * 100).toFixed(2) : ""}
                    onChange={(e) => handleFeeRateInputChange(r, e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* DESKTOP */}
      <div className="hidden md:block">
        <table className="table table-compact w-full rounded-lg border mb-4 border-black/10 dark:border-white/20 bg-white dark:bg-transparent text-[rgba(6,7,21,1)] dark:text-white">
          <thead className="bg-black/5 dark:bg-white/10 text-sm">
            <tr>
              <th>#</th>
              <th>Id</th>
              <th>Logo</th>
              <th>Slider Image</th>
              <th>Title</th>
              <th>Provider</th>
              {canManageFees && <th>Fee rate (%)</th>}
              <th>GEO</th>
              <th>Enabled</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r, i) => (
              <tr key={r.identifier} className="odd:bg-black/[0.02] dark:odd:bg-white/[0.05]">
                <td>{(page - 1) * rows.length + i + 1}</td>
                <td>{r.identifier}</td>
                <td>{r.logo && <img src={r.logo} alt={r.title} className="h-6" />}</td>
                <td>{r.slider_image && <img src={r.slider_image} alt={r.title} className="h-6" />}</td>
                <td>{r.title}</td>
                <td>{r.provider}</td>

                {canManageFees && (
                  <td>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      className="input input-bordered input-xs w-20 bg-white text-black border-black/10 dark:bg-[#0C0C1A] dark:text-white dark:border-white/10"
                      value={Number.isFinite(r.feeRate) ? (r.feeRate * 100).toFixed(2) : ""}
                      onChange={(e) => handleFeeRateInputChange(r, e.target.value)}
                    />
                  </td>
                )}

                <td>
                  <GeoCell provider={r} restriction={restrictionFor(r.identifier)} onOpen={() => onOpenGeo(r)} />
                </td>

                <td>
                  <input type="checkbox" className="toggle toggle-success" checked={r.enabled} onChange={() => onToggle(r)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TablePagination page={page} totalPages={totalPages} onJump={onJump} />
    </CardWrapper>
  )
}
